/* eslint-disable no-empty */
import * as Sentry from '@sentry/react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useAttributePreference, usePreference } from '../../common/util/preferences';
import usePersistedState, { savePersistedState } from '../../common/util/usePersistedState';
import { SwitcherControl } from '../switcher/switcher';
import preloadImages, { mapImages } from './preloadImages';
import useMapStyles from './useMapStyles';

const element = document.createElement('div');
element.style.width = '100%';
element.style.height = '100%';
element.style.boxSizing = 'initial';

export const map = new maplibregl.Map({
  container: element,
  attributionControl: false,
  version: 8,
  name: 'Empty',
  metadata: {
    'mapbox:autocomposite': true,
  },
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
  preserveDrawingBuffer: true,
});

let ready = false;
const readyListeners = new Set();

const addReadyListener = (listener) => {
  readyListeners.add(listener);
  listener(ready);
};

const removeReadyListener = (listener) => {
  readyListeners.delete(listener);
};

const updateReadyValue = (value) => {
  ready = value;
  readyListeners.forEach((listener) => listener(value));
};

const initMap = async () => {
  if (ready) return;
  try {
    // -----------------------------------------------------------------
    // INSTRUMENTACIÓN TEMPORAL — distingue si initMap() está corriendo
    // sobre el estilo placeholder 'Empty' (primera pasada, al montar el
    // mapa) o sobre el estilo real ya aplicado por el switcher (segunda
    // pasada). Sirve para confirmar si MapPositions arranca a pintar
    // posiciones antes de que termine la segunda pasada, que es la que
    // realmente registra los íconos contra el estilo que el usuario ve.
    // -----------------------------------------------------------------
    const styleName = map.getStyle()?.name;
    const stylePass = styleName === 'Empty' ? 'PASADA #1 (estilo Empty)' : `PASADA #2 (estilo real: ${styleName})`;
    console.info(`[MapView] initMap() arrancando — ${stylePass}`, {
      timestamp: new Date().toLocaleTimeString(),
      mapLoaded: map.loaded(),
      isStyleLoaded: map.isStyleLoaded?.(),
      styleId: map.getStyle()?.metadata ?? null,
      layersCount: map.getStyle()?.layers?.length,
      sourcesKeys: Object.keys(map.getStyle()?.sources ?? {}),
    });

    await preloadImages();

    if (!map.hasImage('background')) {
      const missingKeys = [];
      const failedKeys = [];
      Object.entries(mapImages).forEach(([key, value]) => {
        if (!value) {
          missingKeys.push(key);
          return;
        }
        try {
          map.addImage(key, value, {
            pixelRatio: window.devicePixelRatio,
            ...(key === 'background' ? ({ sdf: true }) : ({})),
          });
        } catch (error) {
          // Antes, si esto tiraba (ej. límite de tamaño de textura de
          // la GPU, algo muy dependiente del dispositivo/gama del
          // celular), el forEach se cortaba en seco y TODOS los íconos
          // siguientes en el orden de iteración nunca llegaban a
          // registrarse en el mapa, aunque ya estuvieran perfectamente
          // generados en mapImages. Ahora cada ícono se intenta de
          // forma aislada: si uno falla, seguimos con el resto.
          failedKeys.push(key);
          Sentry.captureException(error, {
            level: 'warning',
            extra: {
              context: 'map.addImage individual',
              key,
              devicePixelRatio: window.devicePixelRatio,
              imageWidth: value?.width,
              imageHeight: value?.height,
              userAgent: navigator.userAgent,
            },
          });
        }
      });

      if (missingKeys.length > 0 || failedKeys.length > 0) {
        Sentry.captureMessage('Íconos de mapa faltantes tras preload', {
          level: 'warning',
          extra: {
            missingKeys,
            failedKeys,
            totalExpected: Object.keys(mapImages).length,
            userAgent: navigator.userAgent,
          },
        });
      }
    } else {
      // INSTRUMENTACIÓN TEMPORAL
      console.info(`[MapView] initMap() — ${stylePass}: map.hasImage('background') ya era true, se salteó el registro de íconos`);
    }

    // INSTRUMENTACIÓN TEMPORAL — marca el instante exacto en que esta
    // pasada de initMap() termina de registrar imágenes en el mapa.
    console.info(`[MapView] initMap() TERMINANDO — ${stylePass}`, {
      timestamp: new Date().toLocaleTimeString(),
      totalImagesRegistradasEnMapa: Object.keys(mapImages).filter((k) => map.hasImage(k)).length,
      totalEsperado: Object.keys(mapImages).length,
    });

    const imagesToLoad = [
      { key: 'arrow', path: '../images/arrow.png' },
      { key: 'arrowW', path: '../images/arrowW.png' },
      { key: 'arrowB', path: '../images/arrowB.png' },
      { key: 'replay-neutral', path: '../images/replay-neutral.png' },
      { key: 'replay-positive', path: '../images/replay-positive.png' },
      { key: 'replay-negative', path: '../images/replay-negative.png' },
      // marcadores playback fijos
      { key: 'replay-start', path: '../images/p_start_en.png' },
      { key: 'replay-end', path: '../images/p_end_en.png' },
      { key: 'replay-stop', path: '../images/stopicon.png' },
    ];

    await Promise.all(
      imagesToLoad.map(async ({ key, path }) => {
        if (map.hasImage(key)) return;

        try {
          const { data } = await map.loadImage(path);
          if (!map.hasImage(key)) {
            map.addImage(key, data);
          }
        } catch (error) {
          console.log(`Error loading image "${key}" from ${path}:`, error);
        }
      })
    );
    updateReadyValue(true);
  } catch (error) {
    Sentry.captureException(error, { extra: { context: 'initMap' } });
  }
};

map.addControl(new maplibregl.NavigationControl());

const switcher = new SwitcherControl(
  () => {
    // INSTRUMENTACIÓN TEMPORAL — marca cuándo el switcher resetea el
    // flag `ready` a false, justo antes de llamar a map.setStyle().
    console.warn('[MapView] switcher onBeforeSwitch: ready -> false (arranca setStyle)', new Date().toLocaleTimeString());
    updateReadyValue(false);
  },
  (styleId) => {
    savePersistedState('selectedMapStyle', styleId);
  },
  () => {
    map.once('styledata', () => {
      // INSTRUMENTACIÓN TEMPORAL
      console.info('[MapView] switcher onAfterSwitch: recibido "styledata", empezando a esperar map.loaded()', new Date().toLocaleTimeString());
      const waiting = () => {
        if (!map.loaded()) {
          setTimeout(waiting, 33);
        } else {
          // INSTRUMENTACIÓN TEMPORAL
          console.info('[MapView] switcher: map.loaded() true, llamando initMap() de nuevo', new Date().toLocaleTimeString());
          initMap();
        }
      };
      waiting();
    });
  },
);

map.addControl(switcher);

map.on('webglcontextlost', () => {
  updateReadyValue(false);
  Sentry.captureMessage('WebGL context lost en el mapa', { level: 'error' });
});

map.on('webglcontextrestored', () => {
  initMap();
});

const MapView = ({ children }) => {
  const containerEl = useRef(null);
  const mapRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);

  const mapStyles = useMapStyles();
  const activeMapStyles = useAttributePreference('activeMapStyles', 'locationIqStreets,osm,carto,googleRoad,googleSatellite,googleTraffic');
  const [defaultMapStyle] = usePersistedState('selectedMapStyle', usePreference('map', 'googleRoad'));
  const mapboxAccessToken = useAttributePreference('mapboxAccessToken');
  const maxZoom = useAttributePreference('web.maxZoom');

  useEffect(() => {
    if (maxZoom) {
      map.setMaxZoom(maxZoom < 23 ? maxZoom : 22);
    }
  }, [maxZoom]);

  useEffect(() => {
    maplibregl.accessToken = mapboxAccessToken;
  }, [mapboxAccessToken]);

  useEffect(() => {
    const filteredStyles = mapStyles.filter((s) => s.available && activeMapStyles.includes(s.id));
    const styles = filteredStyles.length ? filteredStyles : mapStyles.filter((s) => s.id === 'osm');

    switcher.updateStyles(styles, defaultMapStyle);
  }, [defaultMapStyle]);

  useEffect(() => {
    const listener = (ready) => setMapReady(ready);
    addReadyListener(listener);
    return () => {
      removeReadyListener(listener);
    };
  }, []);

  // Watchdog: si a los 15s el mapa todavía no quedó "listo" (ready),
  // algo se coló silenciosamente en initMap()/preloadImages() sin
  // lanzar ninguna excepción capturable. Lo reportamos para no
  // quedarnos otra vez sin ningún rastro en Sentry.
  // Ojo: usamos la variable `ready` del módulo (no el estado `mapReady`
  // de React) para evitar un stale closure dentro del setTimeout.
  useEffect(() => {
    const watchdog = setTimeout(() => {
      if (!ready) {
        Sentry.captureMessage('El mapa no quedó listo (ready) tras 15s', {
          level: 'error',
          extra: { userAgent: navigator.userAgent },
        });
      }
    }, 15000);
    return () => clearTimeout(watchdog);
  }, []);

  useLayoutEffect(() => {
    const currentEl = containerEl.current;
    if (currentEl) {

      if (!currentEl.contains(element)) {
        currentEl.appendChild(element);
      }
      map.resize();

      return () => {

        if (currentEl && currentEl.contains(element)) {
          try {
            currentEl.removeChild(element);
          } catch (e) {
            console.debug('Element already removed from DOM');
          }
        }
      };
    }
  }, [containerEl]);

  useLayoutEffect(() => {
    if (!containerEl.current || mapRef.current) return;

    mapRef.current = map;

    mapRef.current.on('load', async () => {
      await initMap();
      // setMapReady(true);
    });

    return () => {
      //mapRef.current?.remove();
      //mapRef.current = null;
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '100%' }} ref={containerEl}>
      {mapReady && children}
    </div>
  );
};

export default MapView;